/**
 * 文件上传到腾讯云 COS 工具类
 * 支持单文件和多文件上传，使用临时密钥进行安全上传
 */
import notification from 'ant-design-vue/es/notification'
import request from '@/utils/request'
import COS from 'cos-js-sdk-v5'
import { generateFilePath } from '@/api/tool/oss'
import { guid } from '@/utils/ruoyi.js'

export default class UploadFileToOSS {
  static _cachedCredential = null
  static _cachedCredentialExpireAtMs = 0

  static _normalizeExpireAtMs(credentials) {
    const candidates = [
      credentials?.expiredTime,
      credentials?.expireTime,
      credentials?.expiration,
      credentials?.expiredAt,
    ]
    for (const c of candidates) {
      if (!c) continue
      if (typeof c === 'number') {
        // 有的返回秒，有的返回毫秒；秒一般在 1e9 量级
        return c < 1e12 ? c * 1000 : c
      }
      if (typeof c === 'string') {
        const t = Date.parse(c)
        if (!Number.isNaN(t)) return t
      }
    }
    // 兜底：给个较短 TTL，避免一直复用导致过期
    return Date.now() + 5 * 60 * 1000
  }

  static async _getCredential() {
    const now = Date.now()
    if (UploadFileToOSS._cachedCredential && UploadFileToOSS._cachedCredentialExpireAtMs - now > 60 * 1000) {
      return UploadFileToOSS._cachedCredential
    }
    const signatureRes = await request({
      url: '/common/get-cos-signature',
      method: 'get',
      headers: { repeatSubmit: false }
    })
    console.log('-----------')
    if (!signatureRes || !signatureRes.data) {
      notification.error('获取上传凭证失败')
      return null
    }
    const credentials = signatureRes.data
    UploadFileToOSS._cachedCredential = credentials
    UploadFileToOSS._cachedCredentialExpireAtMs = UploadFileToOSS._normalizeExpireAtMs(credentials)
    return credentials
  }

  /**
   * 构造函数
   * @param {Array} files - 文件数组（File 对象数组）
   * @param {Object} callbacks - 回调函数对象
   * @param {Function} callbacks.success - 单个文件上传成功的回调函数，参数为文件信息数组
   * @param {Function} callbacks.finally - 所有文件上传完成的回调函数，参数为所有文件信息数组
   * @param {Function} callbacks.progress - 单个文件上传进度回调 (file, percent[0..1], rawProgress)
   * @param {number} callbacks.concurrency - 并发上传数（默认 3）
   * @throws {Error} 当 files 不是数组时抛出错误
   */
  constructor(files, callbacks) {
    if (!Array.isArray(files)) {
      throw new Error('UploadFileToOSS: files parameter must be an array')
    }

    if (!callbacks || typeof callbacks !== 'object') {
      throw new Error('UploadFileToOSS: callbacks parameter must be an object')
    }

    this.data = [] // 已上传成功的文件列表
    this.successCallback = callbacks.success // 单个文件上传成功回调
    this.completeCallback = callbacks.finally // 所有文件上传完成回调
    this.progressCallback = callbacks.progress // 进度回调
    this.concurrency = Math.max(1, Number(callbacks.concurrency || 3))
    this.files = files // 待上传的文件列表
    this.fileLen = files.length // 文件总数

    this._nextIndex = 0
    this._activeCount = 0
    this._doneCount = 0
    this._progressEmitAt = new Map()

    this._start()
  }

  /**
   * 启动并发上传
   */
  async _start() {
    if (this.fileLen === 0) {
      if (this.completeCallback && typeof this.completeCallback === 'function') {
        this.completeCallback([])
      }
      return
    }
    while (this._activeCount < this.concurrency && this._nextIndex < this.fileLen) {
      this._uploadOne(this._nextIndex++)
    }
  }

  async _uploadOne(index) {
    this._activeCount++
    const currentFile = this.files[index]
    try {
      const credentials = await UploadFileToOSS._getCredential()
      if (!credentials) {
        this._finishOne()
        return
      }

      const fileName = currentFile?.name || `file_${Date.now()}`
      const suffix = fileName.substring(fileName.lastIndexOf('.')) || ''
      const filePath = generateFilePath('upload', suffix)

      const bucket = credentials.bucket
      const region = credentials.region
      const staticUrl = credentials.resultUrl || `https://${bucket}.cos.${region}.myqcloud.com/`

      const cos = new COS({
        SecretId: credentials.tmpSecretId || credentials.accessKeyId,
        SecretKey: credentials.tmpSecretKey || credentials.accessKeySecret,
        XCosSecurityToken: credentials.sessionToken || credentials.securityToken,
      })

      const uploadResult = await cos.uploadFile({
        Bucket: bucket,
        Region: region,
        Key: filePath,
        Body: currentFile,
        SliceSize: 1024 * 1024 * 5,
        onProgress: (progressData) => {
          if (!this.progressCallback) return
          const now = Date.now()
          const last = this._progressEmitAt.get(currentFile) || 0
          if (now - last < 150) return
          this._progressEmitAt.set(currentFile, now)
          this.progressCallback(currentFile, progressData?.percent ?? 0, progressData)
        },
      })

      if (uploadResult?.statusCode === 200) {
        const fileUrl = staticUrl + filePath
        const fileItem = {
          uid: guid(8, 10),
          status: 'done',
          name: fileName,
          halfUrl: filePath,
          path: fileUrl,
          url: fileUrl,
          cover: ''
        }
        this.data.push(fileItem)

        if (this.successCallback && typeof this.successCallback === 'function') {
          this.successCallback([fileItem])
        }
      } else {
        notification.error(`上传失败，状态码：${uploadResult?.statusCode ?? 'unknown'}`)
      }
    } catch (error) {
      const errorMessage = error?.message || error?.toString?.() || '未知错误'
      notification.error(`上传失败：${errorMessage}`)
    } finally {
      this._finishOne()
    }
  }

  _finishOne() {
    this._activeCount = Math.max(0, this._activeCount - 1)
    this._doneCount++
    if (this._doneCount >= this.fileLen) {
      if (this.completeCallback && typeof this.completeCallback === 'function') {
        this.completeCallback(this.data)
      }
      return
    }
    if (this._nextIndex < this.fileLen) {
      this._uploadOne(this._nextIndex++)
    }
  }
}